import { AfterViewInit, Component, Inject, NgZone, OnDestroy, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import { AudioService } from 'src/app/services/audio.service';
import { DevicesManagerService } from 'src/app/services/devices-manager.service';
import { RtcServiceController } from 'src/app/services/rtc.service';
import {
  DisableIncomingVideosSource,
  SessionSharedDataService,
} from 'src/app/services/session-shared-data.service';
import { UiService } from 'src/app/services/ui.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { DomListenerFactoryService } from 'src/app/services/dom-listener-factory.service';
import { PROMPTS } from 'src/app/common/utils/call-states-constants';
import { DeviceAndBrowserDetectorService } from 'src/app/services/device-and-browser-detector.service';
import { CallRelatedModalAction, DeviceErrorType } from 'src/app/models/device-manger';
import { VirtualBackgroundInsertableStreamService } from 'src/app/services/virtual-background-insertable-stream.service';
import { LocalTracksManagerService } from 'src/app/services/local-tracks-manager.service';
import { TelemetryService } from 'src/app/services/telemetry.service';
import { NoiseCancellationConfigurationService } from 'src/app/services/noise-cancellation-configuration.service';
import { FlagsService } from 'src/app/services/flags.service';
import { UserService } from 'src/app/services/user.service';
import { CustomerSupportService } from 'src/app/services/customer-support.service';
import { MusicModeConfigurationService } from 'src/app/services/music-mode-configuration.service';
import {
  DevicesSettingsBaseComponent,
  DevicesType,
} from '../devices-settings/devices-settings-base.component';
@UntilDestroy()
@Component({
  selector: 'app-space-device-modal',
  templateUrl: './space-device-modal.component.html',
  styleUrls: ['./space-device-modal.component.scss'],
})
export class SpaceDeviceModalComponent
  extends DevicesSettingsBaseComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  readonly NO_INPUT = ' (No input detected)';

  deviceErrorType = DeviceErrorType;
  deviceType = DevicesType;

  isSettingDevices = false;

  disableVideos = false;

  disableVideoStreamsSubMsg = '';
  disabledThroughNotification = false;

  isComponentDestroyed = false;

  prompts = PROMPTS;

  constructor(
    private dialogRef: MatDialogRef<SpaceDeviceModalComponent>,
    sessionSharedDataService: SessionSharedDataService,
    ngZone: NgZone,
    rtcServiceController: RtcServiceController,
    audioService: AudioService,
    devicesManagerService: DevicesManagerService,
    uiService: UiService,
    deviceDetectorService: DeviceDetectorService,
    userService: UserService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      returnBackToJoinCallModal: boolean;
      deviceErrorType?: DeviceErrorType;
      audioOnly?: boolean;
    },
    protected translateService: TranslateService,
    domListenerFactoryService: DomListenerFactoryService,
    deviceAndBrowserDetectorService: DeviceAndBrowserDetectorService,
    virtualBackgroundInsertableStreamService: VirtualBackgroundInsertableStreamService,
    localTrackManagerService: LocalTracksManagerService,
    telemetryService: TelemetryService,
    private noiseCancellationConfigService: NoiseCancellationConfigurationService,
    protected flagsService: FlagsService,
    protected customerSupportService: CustomerSupportService,
    private musicModeConfigService: MusicModeConfigurationService,
  ) {
    super(
      domListenerFactoryService,
      deviceDetectorService,
      uiService,
      rtcServiceController,
      devicesManagerService,
      ngZone,
      deviceAndBrowserDetectorService,
      audioService,
      PROMPTS.SPACE_DEVICE_MODAL,
      translateService,
      virtualBackgroundInsertableStreamService,
      sessionSharedDataService,
      localTrackManagerService,
      telemetryService,
      true,
      flagsService,
      userService,
      customerSupportService,
    );
    this.audioOnly = Boolean(data.audioOnly);

    this.disabledThroughNotification =
      this.sessionSharedDataService.disabledThroughWeakNotification;
    this.sessionSharedDataService.disableVideoStreamChanged
      .pipe(untilDestroyed(this))
      .subscribe((newDisableVideoStreams) => {
        this.disableVideos = newDisableVideoStreams.disabled;
      });

    this.handleDisableVideoStreamsSubMsg();

    if (data.deviceErrorType) {
      this.troubleShootDevice(data.deviceErrorType);
    }
    this.localTrackManagerService.isCallTracksNeededByCallModal = true;
  }

  async ngOnInit() {
    super.ngOnInit();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  ngOnDestroy(): void {
    this.localTrackManagerService.isCallTracksNeededByCallModal = false;
    super.ngOnDestroy();
  }

  // override
  getComponentPromptLayout(): PROMPTS {
    return PROMPTS.SPACE_DEVICE_MODAL;
  }

  // override
  closeDialog() {
    this.done();
  }

  // override
  showVirtualBackgroundsModal(): void {
    this.dialogRef.close(CallRelatedModalAction.OPEN_VIRTUAL_BACKGROUND_MODAL);
  }

  async done() {
    this.sessionSharedDataService.disableVideoStreamChanged.next({
      disabled: this.disableVideos,
      source: DisableIncomingVideosSource.USER,
    });
    this.rtcServiceController.service.isNoiseCancellationEnabled = this.enableNoiseCancellation;
    this.noiseCancellationConfigService.saveNoiseCancellationState(this.enableNoiseCancellation);
    this.rtcServiceController.service.isMusicModeEnabled = this.enableMusicMode;
    this.musicModeConfigService.saveMusicModeState(this.enableMusicMode);
    this.sessionSharedDataService.disabledThroughWeakNotification = false;
    this.dialogRef.close();
  }

  onChangeDisableVideoStreams() {
    this.handleDisableVideoStreamsSubMsg();
  }

  private handleDisableVideoStreamsSubMsg() {
    if (this.disableVideos) {
      this.disableVideoStreamsSubMsg = this.sessionSharedDataService.disabledThroughWeakNotification
        ? this.translateService.instant(
            'You opted to turn this on after we detected degraded call performance. You can turn this off at any time',
          )
        : this.translateService.instant(
            'You opted to turn this on. Participant screenshares always stay visible',
          );
    } else {
      this.disableVideoStreamsSubMsg = this.translateService.instant(
        'Participant screenshares always stay visible',
      );
    }
  }
}
